import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import useScopedTranslation from 'hooks/useScopedTranslation';
import { contractGenI18n } from './i18n';
import StepClient from './Steps/StepClient';
import StepCase from './Steps/StepCase';
import StepCosts from './Steps/StepCosts';
import StepTasks from './Steps/StepTasks';
import StepContract from './Steps/StepContract';
import StepperHorizontal from 'components/StepperaHorizontal/StepperHorizontal';
import ConfirmModal from 'components/ConfirmModal/ConfirmModal';
import { Editor } from '@tinymce/tinymce-react';
import { TinyMCE_ApiKey } from 'config';
import {
  createContractWizardDraft,
  finishContractWizard,
  getContractCatalogs,
  getContractWizardState,
  saveContractWizardStep,
} from '../../services/contractGeneratorService';

const EMPTY_FORM = {
  cliente: null,
  lineaDeNegocio: null,
  servicio: null,
  caso: {},
  costos: {},
  tareas: [],
  contrato: {},
};

const ContractGenerator = ({ DesignSystemComponents }) => {
  const { Card, Button, SvgIcon, Modal, Badge, Toast } = DesignSystemComponents;
  const { user } = useAuth();
  const { t } = useScopedTranslation(contractGenI18n);
  const editorBackgroundRef = useRef(null);
  const location = useLocation();
  const routeContractId = location?.state?.contractId || null;

  const [currentStep, setCurrentStep] = useState(0);
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [loading, setLoading] = useState(false);
  const [showBackground, setShowBackground] = useState(false);
  const [withInfoClient] = useState(true);
  const [clientActive] = useState(true);
  const [canActiveClient] = useState(false);
  const [contractId, setContractId] = useState(routeContractId);
  const [catalogs, setCatalogs] = useState({
    businessLines: [],
    services: [],
    statuses: [],
    lawyers: [],
  });

  const steps = [
    {
      label: t('cg_step_client'),
      component: StepClient,
      icon: <SvgIcon name='user' size='20px' />,
    },
    {
      label: t('cg_step_case'),
      component: StepCase,
      icon: <SvgIcon name='briefcase' size='20px' />,
    },
    {
      label: t('cg_step_costs'),
      component: StepCosts,
      icon: <SvgIcon name='dollarSign' size='20px' />,
    },
    {
      label: t('cg_step_tasks'),
      component: StepTasks,
      icon: <SvgIcon name='squareCheck' size='20px' />,
    },
    {
      label: t('cg_step_contract'),
      component: StepContract,
      icon: <SvgIcon name='fileEarmarkText' size='20px' />,
    },
  ];

  const StepComponent = steps[currentStep].component;

  const loadCatalogs = async () => {
    try {
      const data = await getContractCatalogs();
      setCatalogs({
        businessLines: (data?.businessLines || []).map((x) => ({
          label: x.label,
          value: x.id,
        })),
        services: (data?.services || []).map((x) => ({
          label: x.label,
          value: x.id,
        })),
        statuses: data?.statuses || [],
        lawyers: (data?.lawyers || []).map((x) => ({ label: x.label, value: x.id })),
      });
    } catch (error) {
      console.error(error);
    }
  };

  const loadWizardState = async (id) => {
    if (!id) return;
    try {
      const state = await getContractWizardState(id);
      const payload = state?.payloadJson ? JSON.parse(state.payloadJson) : null;
      if (payload && typeof payload === 'object') {
        setFormData((prev) => ({ ...prev, ...payload, contractId: id, id }));
      } else {
        // No payload saved yet — still set contractId so StepContract can use it
        setFormData((prev) => ({ ...prev, contractId: id, id }));
      }
      if (typeof state?.currentStep === 'number') {
        // Backend enum is 1-based (Client=1..Contract=5), frontend uses 0-based index
        const zeroBasedStep = Math.max(0, Math.min(4, Number(state.currentStep) - 1));
        setCurrentStep(zeroBasedStep);
        setMaxStepReached(zeroBasedStep);
      }
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    loadCatalogs();
  }, []);

  useEffect(() => {
    if (routeContractId) {
      setContractId(routeContractId);
      loadWizardState(routeContractId);
    } else {
      // Reset when navigating to create a new contract (no contractId in route state)
      setContractId(null);
      setCurrentStep(0);
      setMaxStepReached(0);
      setFormData(EMPTY_FORM);
    }
  }, [routeContractId]);

  const ensureDraftContract = async () => {
    if (contractId) return contractId;
    const draft = await createContractWizardDraft({
      clientId: formData?._clientId || formData?.cliente?.value || formData?.cliente?.id || null,
      businessLineId: formData?._businessLineId || formData?.lineaDeNegocio?.value || null,
      serviceId: formData?._serviceId || formData?.servicio?.value || null,
      initialStep: currentStep + 1, // Backend enum ContractWizardStep starts at 1 (Client=1, Case=2, ...)
      payloadJson: JSON.stringify(formData),
    });
    setContractId(draft.contractId);
    setFormData((prev) => ({ ...prev, contractId: draft.contractId, id: draft.contractId }));
    return draft.contractId;
  };

  const saveStep = async (stepIndex, nextData = formData) => {
    const id = await ensureDraftContract();
    await saveContractWizardStep(id, {
      currentStep: stepIndex + 1, // Backend enum ContractWizardStep starts at 1 (Client=1, Case=2, ...)
      payloadJson: JSON.stringify(nextData),
      isCompleted: false,
    });
  };

  const handlePrev = async () => {
    const nextStep = Math.max(0, currentStep - 1);
    setCurrentStep(nextStep);
    try {
      await saveStep(nextStep);
    } catch (error) {
      console.error(error);
    }
  };

  const proceedToNextStep = async () => {
    const nextStep = Math.min(steps.length - 1, currentStep + 1);
    try {
      await saveStep(nextStep);
      setCurrentStep(nextStep);
      setMaxStepReached((prev) => Math.max(prev, nextStep));
    } catch (error) {
      console.error(error);
      if (Toast?.show) {
        Toast.show({
          title: t('cg_toast_error'),
          message:
            error?.customMessage ||
            error?.message ||
            t('cg_toast_save_error'),
          variant: 'error',
        });
      }
    }
  };

  const handleNext = async () => {
    if (!isCurrentStepValid()) {
      if (Toast?.show) {
        Toast.show({
          title: t('cg_toast_missing_data'),
          message: getStepValidationMessage(),
          variant: 'warning',
        });
      }
      return;
    }

    if (needsNoCaseConfirmation()) {
      setShowConfirmNoCaseNoCreditor(true);
      return;
    }

    await proceedToNextStep();
  };

  const resolveId = (obj) => {
    if (!obj) return 0;
    if (Array.isArray(obj)) obj = obj[0];
    if (typeof obj === 'string' || typeof obj === 'number') {
      const num = Number(obj);
      return Number.isFinite(num) && num > 0 ? num : 0;
    }
    const val = obj?.value ?? obj?.id ?? obj?.clientId ?? 0;
    const num = Number(val);
    return Number.isFinite(num) && num > 0 ? num : 0;
  };

  const resolveFieldId = (explicitId, comboObj) => {
    const explicit = Number(explicitId);
    if (Number.isFinite(explicit) && explicit > 0) return explicit;
    return resolveId(comboObj);
  };

  const handleFinish = async () => {
    setLoading(true);
    try {
      const id = await ensureDraftContract();
      const caso = formData?.caso || {};
      const costos = formData?.costos || {};
      const contrato = formData?.contrato || {};

      const clientId = resolveFieldId(formData?._clientId, formData?.cliente);
      const businessLineId = resolveFieldId(formData?._businessLineId, formData?.lineaDeNegocio);
      const serviceId = resolveFieldId(formData?._serviceId, formData?.servicio);

      const bodyToSend = new FormData();
      bodyToSend.append('ClientId', String(clientId));
      bodyToSend.append('BusinessLineId', String(businessLineId));
      bodyToSend.append('ServiceId', String(serviceId));
      bodyToSend.append('CreatorId', String(user?.userId || 0));
      bodyToSend.append('CaseIsNew', String(caso.tipo === 'nuevo'));
      bodyToSend.append('CaseId', String(caso.caseId || 0));
      bodyToSend.append('CaseRol', caso.code || '');
      bodyToSend.append('CaseName', caso.nombreCaso || caso.title || '');
      bodyToSend.append('TribunalId', String(caso.tribunalId || 0));
      bodyToSend.append('Demandante', caso.demandante || '');
      bodyToSend.append('TotalAmount', String(Number(costos.costoTotal || 0)));
      bodyToSend.append('NumberOfFees', String(Number(costos.numeroCuotas || 1)));
      bodyToSend.append(
        'FeeAmount',
        String(Number(costos.valorPorCuota || costos.costoTotal || 0))
      );
      bodyToSend.append('FirstPaymentDate', costos.fechaPago || '');
      bodyToSend.append('Mode', contrato.modo || 'manual');

      if ((contrato.modo || 'manual') === 'manual') {
        bodyToSend.append('Html', contrato.htmlManual || '');
      }
      if (contrato.modo === 'archivo' && contrato.file) {
        bodyToSend.append('File', contrato.file);
      }

      (formData?.tareas || [])
        .filter((t) => t.selected)
        .forEach((t, i) => {
          bodyToSend.append(`Tasks[${i}].Nombre`, t.nombre || `Tarea ${i + 1}`);
          bodyToSend.append(`Tasks[${i}].Description`, t.description || '');
          bodyToSend.append(`Tasks[${i}].AssignedUserId`, String(resolveId(t.asignado)));
          bodyToSend.append(`Tasks[${i}].DueDate`, t.fecha || '');
        });

      const res = await finishContractWizard(id, bodyToSend);
      if (Toast?.show) {
        Toast.show({
          title: t('cg_toast_contract_generated'),
          message: t('cg_toast_contract_generated_msg', { id: res.contractId }),
          variant: 'success',
        });
      } else {
        alert(t('cg_alert_contract_generated', { id: res.contractId }));
      }
    } catch (err) {
      console.error(err);
      if (Toast?.show) {
        Toast.show({
          title: t('cg_toast_error'),
          message:
            err?.customMessage || err?.message || t('cg_toast_contract_error'),
          variant: 'error',
        });
      } else {
        alert(t('cg_alert_contract_error'));
      }
    } finally {
      setLoading(false);
    }
  };

  const [maxStepReached, setMaxStepReached] = useState(0);
  const [showConfirmNoCaseNoCreditor, setShowConfirmNoCaseNoCreditor] = useState(false);

  const canNavigateTo = (index) => index <= maxStepReached;

  const needsNoCaseConfirmation = () => {
    if (currentStep !== 1) return false;
    const activeCase = formData?.activeCase ?? true;
    const activeCreditor = formData?.activeCreditor ?? false;
    if (activeCase || activeCreditor) return false;
    const hasBusinessLine = Boolean(formData?.lineaDeNegocio?.value);
    const hasService = Boolean(formData?.servicio?.value);
    return hasBusinessLine && hasService;
  };

  const isCurrentStepValid = () => {
    if (currentStep === 0) {
      return Boolean(formData?.cliente);
    }

    if (currentStep === 1) {
      const hasBusinessLine = Boolean(formData?.lineaDeNegocio?.value);
      const hasService = Boolean(formData?.servicio?.value);
      if (!hasBusinessLine || !hasService) return false;

      const activeCase = formData?.activeCase ?? true;
      const activeCreditor = formData?.activeCreditor ?? false;

      // Neither active — valid but requires confirmation
      if (!activeCase && !activeCreditor) return true;

      if (activeCase) {
        const selectedIds = (formData?.selectedCaseIds || []).filter(
          (id) => Number.isFinite(Number(id)) && Number(id) > 0
        );
        return selectedIds.length > 0;
      }

      if (activeCreditor) {
        return (formData?.creditors || []).length > 0;
      }

      return false;
    }

    if (currentStep === 2) {
      const costos = formData?.costos || {};
      const total = Number(costos.costoTotal || 0);
      if (total <= 0) return false;
      const hasFee = Boolean(costos.hasFee);
      if (hasFee) {
        return Number(costos.numeroCuotas || 0) > 0;
      }
      // Without installments, require payment date
      return Boolean(costos.fechaPago);
    }

    return true;
  };

  const getStepValidationMessage = () => {
    if (currentStep === 0) {
      return t('cg_validation_select_client');
    }
    if (currentStep === 1) {
      if (!formData?.lineaDeNegocio?.value || !formData?.servicio?.value) {
        return t('cg_validation_select_bl_service');
      }
      const activeCase = formData?.activeCase ?? true;
      const activeCreditor = formData?.activeCreditor ?? false;
      if (activeCase) {
        return t('cg_validation_select_case');
      }
      if (activeCreditor) {
        return t('cg_validation_add_creditor');
      }
      return t('cg_validation_select_case_or_creditor');
    }
    if (currentStep === 2) {
      const costos = formData?.costos || {};
      if (Number(costos.costoTotal || 0) <= 0) return t('cg_validation_enter_total_cost');
      if (costos.hasFee && Number(costos.numeroCuotas || 0) <= 0) return t('cg_validation_enter_fees');
      if (!costos.hasFee && !costos.fechaPago) return t('cg_validation_enter_payment_date');
    }
    return t('cg_validation_complete_info');
  };

  return (
    <div>
      <div className='mb-4'>
        <div className='d-flex justify-content-between'>
          <div>
            <h2 className=' fw-semibold'>{t('cg_title')}</h2>
            <h4 className=' fw-normal'>
              {t('cg_subtitle')}
            </h4>
          </div>
          <div>
            <Button
              label={t('cg_btn_background')}
              size='sm'
              variant='primary'
              onClick={() => setShowBackground(true)}
            />
          </div>
        </div>
        <div className='my-3'>
          <StepperHorizontal
            steps={steps}
            currentStep={currentStep}
            onStepClick={(index) => {
              if (canNavigateTo(index)) setCurrentStep(index);
            }}
            canNavigateTo={canNavigateTo}
          />
        </div>
      </div>

      <Card specialPadding='p-4'>
        <StepComponent
          DesignSystemComponents={DesignSystemComponents}
          formData={formData}
          catalogs={catalogs}
          contractId={contractId}
          onChange={(data) => setFormData((prev) => ({ ...prev, ...data }))}
          onPrev={handlePrev}
          isFirst={currentStep === 0}
          isLast={currentStep === steps.length - 1}
        />
        <div className='d-flex justify-content-between align-items-center'>
          <div style={{ visibility: currentStep === 0 && 'hidden' }}>
            <Button label={t('cg_btn_prev')} variant='secondary' onClick={handlePrev} />
          </div>
          {currentStep !== steps.length - 1 && (
            <Button
              label={t('cg_btn_next')}
              variant='primary'
              onClick={handleNext}
              disabled={!isCurrentStepValid()}
            />
          )}
          {currentStep === steps.length - 1 && (
            <Button
              label={loading ? t('cg_btn_finishing') : t('cg_btn_finish')}
              variant='primary'
              onClick={handleFinish}
              disabled={loading}
            />
          )}
        </div>
      </Card>

      <ConfirmModal
        Modal={Modal}
        show={showConfirmNoCaseNoCreditor}
        title={t('cg_confirm_next_step')}
        message={t('cg_confirm_no_case_no_creditor')}
        confirmLabel={t('cg_confirm_yes_continue')}
        confirmVariant='warning'
        onConfirm={async () => {
          setShowConfirmNoCaseNoCreditor(false);
          await proceedToNextStep();
        }}
        onClose={() => setShowConfirmNoCaseNoCreditor(false)}
      />

      <Modal
        onClose={() => setShowBackground(false)}
        primaryBtnAction={() => {
          const html = editorBackgroundRef.current?.getContent() || '';
          setFormData((prev) => ({ ...prev, hojaAtencion: html }));
          setShowBackground(false);
        }}
        primaryBtnTxt={t('cg_btn_save')}
        disabledPrimaryBtn={withInfoClient && !canActiveClient && !clientActive}
        secondaryBtnAction={() => setShowBackground(false)}
        secondaryBtnTxt={t('cg_btn_cancel')}
        show={showBackground}
        title={t('cg_modal_background_title')}
        width='80vw'
      >
        <div className='row'>
          <div className='col-12'>
            {withInfoClient && !canActiveClient && !clientActive && (
              <div className='warning-message'>
                {t('cg_inactive_client_warning')}
              </div>
            )}
          </div>
        </div>
        <div className='row'>
          <div className='col-8'>
            <p className='body mb-3 fw-bold'>{t('cg_attention_sheet')}</p>
            <Editor
              apiKey={TinyMCE_ApiKey}
              onInit={(_, editor) => (editorBackgroundRef.current = editor)}
              value={formData?.hojaAtencion || ''}
              onEditorChange={() => {}}
              init={{
                height: 400,
                menubar: true,
                plugins:
                  'advlist autolink lists link charmap preview anchor ' +
                  'searchreplace visualblocks code fullscreen ' +
                  'insertdatetime table code help wordcount',
                toolbar:
                  'undo redo | formatselect | bold italic underline | ' +
                  'alignleft aligncenter alignright | ' +
                  'bullist numlist outdent indent | removeformat',
                branding: false,
                language: 'es',
              }}
            />
          </div>
          <div className='col-4'>
            {withInfoClient ? (
              <Card specialPadding='p-4'>
                <div className='d-flex justify-content-between'>
                  <div className='me-3'>
                    <p className='body mb-2'>
                      <span className='fw-bold'>{t('cg_label_name')}</span>
                      {formData?.cliente?.name || formData?.cliente?.label || '-'}
                    </p>
                    <p className='body mb-2'>
                      <span className='fw-bold'>{t('cg_label_rut')}</span>
                      {formData?.cliente?.rut || '-'}
                    </p>
                    <p className='body mb-2'>
                      <span className='fw-bold'>{t('cg_label_email')}</span>
                      {formData?.cliente?.email || '-'}
                    </p>
                    <p className='body mb-2'>
                      <span className='fw-bold'>{t('cg_label_phone')}</span>
                      {formData?.cliente?.phone || '-'}
                    </p>
                    <p className='body mb-2'>
                      <span className='fw-bold'>{t('cg_label_lawyer')}</span>
                      {formData?.abogado?.label || '-'}
                    </p>
                    <p className='body mb-2'>
                      <span className='fw-bold'>{t('cg_label_business_line')}</span>
                      {formData?.lineaDeNegocio?.label || '-'}
                    </p>
                    <p className='body mb-2'>
                      <span className='fw-bold'>{t('cg_label_service')}</span>
                      {formData?.servicio?.label || '-'}
                    </p>
                    <p className='body mb-2'>
                      <span className='fw-bold'>{t('cg_label_contract_code')}</span>
                      {contractId || '-'}
                    </p>
                  </div>
                  <div>
                    {!canActiveClient && !clientActive && (
                      <Badge text={t('cg_badge_inactive')} color='light' size='sm' />
                    )}
                  </div>
                </div>
              </Card>
            ) : (
              <p className='body'>
                {t('cg_no_client_info')}
              </p>
            )}
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default ContractGenerator;
